import React from 'react';

import { BackofficeUser, BackofficeUserInfo } from '../../../../../models/UserModels';
import { DataTable, DataTableColumn, StatusBadge } from '../../../../../components/Admin';
import { useWhitelabel } from '../../../../../hooks/useWhitelabel';
import { SUBDOMAIN_ENUM } from '../../../../../constants/subddomainEnum';

import { Stack, Primary, Secondary } from './styles';

interface IUsersTable {
  data: BackofficeUserInfo[];
}

export const isVerified = (user: BackofficeUser) => user.status !== 'waitingVerification';

const location = ({ address }: BackofficeUserInfo) =>
  [address?.city, address?.state].filter(Boolean).join(' - ');

const yesNo = (value: boolean, label: string) => (
  <StatusBadge
    label={value ? 'Sim' : 'Não'}
    tone={value ? 'positive' : 'neutral'}
    ariaLabel={`${label}: ${value ? 'sim' : 'não'}`}
  />
);

const UsersTable: React.FC<IUsersTable> = ({ data }) => {
  const { subdomain } = useWhitelabel();

  const isChurch = subdomain() === SUBDOMAIN_ENUM.MARTINBUCER;

  const columns: DataTableColumn<BackofficeUserInfo>[] = [
    {
      key: 'user',
      header: 'Usuário',
      align: 'start',
      render: ({ user }) => (
        <Stack>
          <Primary>{user.fullname}</Primary>
          <Secondary>{user.email}</Secondary>
        </Stack>
      ),
    },
    {
      key: 'contact',
      header: 'Contato',
      align: 'start',
      render: info => (
        <Stack>
          <span>{info.user.cellphone || '—'}</span>
          {location(info) && <Secondary>{location(info)}</Secondary>}
        </Stack>
      ),
    },
    {
      key: 'verified',
      header: 'E-mail',
      render: ({ user }) =>
        isVerified(user) ? (
          <StatusBadge label="Verificado" tone="positive" />
        ) : (
          <StatusBadge label="Pendente" tone="warning" />
        ),
    },
  ];

  if (isChurch) {
    columns.push(
      {
        key: 'signed',
        header: 'Contrato assinado',
        render: ({ user }) => yesNo(!!user.clicksign_is_signed, 'Contrato assinado'),
      },
      {
        key: 'paid',
        header: 'Comprou e pagou',
        render: ({ user }) => yesNo(user.purchases > 0, 'Comprou e pagou'),
      },
      {
        key: 'church',
        header: 'Igreja',
        align: 'start',
        render: ({ user }) => user.schoolpartner || '—',
      },
    );
  }

  return (
    <DataTable
      columns={columns}
      rows={data}
      rowKey={({ user }) => user.userid}
      emptyMessage="Nenhum usuário encontrado."
    />
  );
};

export default UsersTable;
